import { create } from 'zustand'
import { produce } from 'immer'
import productStore from './product'
import { persist } from 'zustand/middleware'
import notificationStore from './notification';
import purchaseOrderStore from './purchase_order';

const initialTransaction = {
  items_attributes: [],
  po_number: "",
  purchase_order_id: null,
  expected_on: "",
  notes: "",
  category: null,
  destinations_transactions_attributes: [],
  region_id: null,
  lastRegionUUID: null,
  urgent: false,
  state: 'order',
  direction: -1,
}

export default create(
  persist(
    (set, get) => ({
      locationId: null,
      saving: false,
      setLocationId(locationId) {
        set({ locationId: locationId })
      },
      errors: [],
      clearErrors() {
        set({ errors: [] })
      },
      transactionToEdit: initialTransaction,
      setTransactionToEdit(transaction) {
        if (typeof transaction == 'function') {
          return set({transaction: transaction(get().transactionToEdit)})
        }
        set({ transactionToEdit: transaction })
      },
      total: 0,
      setTotal(total) {
        set({ total: total })
      },
      page: 1,
      collection: [],
      setCollection(collection) {
        set({ collection: collection })
      },
      purchase_order_id: null,
      setPage(page) {
        set({ page: page })
      },
      setPurchaseOrderId(value) {
        set({ purchase_order_id: value })
      },
      filters: {
        destination_id: '',
        reference: '',
        order_name: '',
        magento_order_id: '',
      },
      setFilters(filters) {
        set({ filters: {...get().filters, ...filters} })
      },
      add(product, quantity = 1) {
        const newTransaction = produce(get().transactionToEdit ?? {}, draft => {
          draft.items_attributes = draft.items_attributes ?? []
          draft.location_id = draft.location_id ?? get().locationId
          const existingItemIndex = draft.items_attributes.findIndex(item => item.product_id == product.id)
          if (existingItemIndex >= 0) {
            draft.items_attributes[existingItemIndex].quantity += 1
          } else {
            draft.items_attributes.push({
              product: product,
              product_id: product.id,
              quantity: quantity,
            })
          }
        })
        set({ transactionToEdit: newTransaction })
        notificationStore.getState().add({
          message: `${product.title} has been added`,
          type: 'success'
        })
      },
      async delete({id}) {
        await fetch(`/api/inventory_transactions/${id}`, {
          method: 'DELETE'
        })
        const newTransactions = [...get().collection]
        const index = newTransactions.findIndex(t => t.id == id)
        newTransactions.splice(index, 1)
        if (index >= 0) {
          set({ collection: newTransactions })
          notificationStore.getState().add({
            message: `Transaction deleted`,
            type: 'success'
          })
        }
        const newPurchaseOrders = [...purchaseOrderStore.getState().collection]
        const po_index = newPurchaseOrders.findIndex(t => t.id == id)
        if (po_index >= 0) {
          newPurchaseOrders.splice(po_index, 1)
          purchaseOrderStore.getState().setCollection(newPurchaseOrders)
          notificationStore.getState().add({
            message: `Purchase order deleted`,
            type: 'success'
          })
        }
      },
      clear() {
        set({ transactionToEdit: initialTransaction, errors: [] })
      },
      // it might seem strange that this function takes in a transaction object instead
      // of just using the transactionToEdit from the store, but this is to allow the ops screen to
      // have it's own transaction object that it can edit and then save. the reason for this is that
      // the ops screen has a different set of fields that it needs to validate and save
      async update({id, ...transaction}) {
        set({ saving: true })
        const response = await fetch(`/api/inventory_transactions/${id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({inventory_transaction: transaction})
        })
        const data = await response.json()
        const newTransactions = [...get().collection]
        const index = newTransactions.findIndex(t =>  t.id == data.id)
        if (index >= 0) {
          newTransactions.splice(index, 1, data)
          set({ collection: newTransactions })
          notificationStore.getState().add({
            message: `Transaction updated`,
            type: 'success'
          })
        }
        const newPurchaseOrders = [...purchaseOrderStore.getState().collection]
        const po_index = newPurchaseOrders.findIndex(t => t.id == data.id)
        if (po_index >= 0) {
          newPurchaseOrders.splice(po_index, 1, data)
          purchaseOrderStore.getState().setCollection(newPurchaseOrders)
          notificationStore.getState().add({
            message: `Purchase order updated`,
            type: 'success'
          })
        }
        get().clear()
        set({ saving: false })
      },
      async create() {
        if (!(await get().validate(get().transactionToEdit))) {
          return false
        }

        set({ saving: true })

        const collection = [...get().collection]
        const po_collection = [...purchaseOrderStore.getState().collection]
        try {
          const response = await fetch('/api/inventory_transactions.json', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({inventory_transaction: get().transactionToEdit})
          })
          if (!response.ok) {
            throw new Error(response)
          }
          const data = await response.json()
          if (data.direction == 0 ) {
            po_collection.unshift(data)
          } else if (get().page == 1) {
            collection.unshift(data)
          }
          if (data.purchase_order_id) {
            const transactionIndex = po_collection.findIndex(transaction => transaction.id == data.purchase_order_id)
            po_collection.splice(transactionIndex, 1, data.purchase_order)
          }
          set({collection: collection})
          purchaseOrderStore.getState().setCollection(po_collection)
          get().clear()
          productStore.getState().updates(data.items_attributes.map(item => item.product))
          notificationStore.getState().add({
            message: `Transaction saved`,
            type: 'success'
          })
        } catch (err) {
          console.log(err)
          notificationStore.getState().add({
            message: `There has been an error`,
            type: 'error'
          })
        } 
        set({ saving: false })
      },
      async save() {
        const transaction = get().transactionToEdit
        if (transaction.id && await get().validate(transaction)) {
          return get().update(transaction)
        } else {
          return get().create()
        }
      },
      async validate(transaction, options = {}) {
        let total = 100
        const errors = []
        const uniqueDestinations = new Set()
        const {requireOrderNumber = true, ops = false} = options 

        const notDeletedDestinations = transaction.destinations_transactions_attributes.filter(dt => !dt['_destroy'])

        if (notDeletedDestinations.length > 0) {
          total = 0
          await notDeletedDestinations.forEach((dt) => {
            if (dt.percentage) {
              total += parseInt(dt.percentage)
            }
            if (!dt.destination_id) {
              errors.push('Destination is required')
            }
            if (!dt.percentage) {
              errors.push('Percentage is required')
            }
            if (dt.percentage > 100) {
              errors.push('Percentage can\'t be greater than 100')
            }
            if (dt.percentage < 1) {
              errors.push('Percentage can\'t be less than 1')
            }
            if (uniqueDestinations.has(dt.destination_id)) {
              errors.push("Can't have duplicate destinations")
            }
            uniqueDestinations.add(dt.destination_id)
          })
        }

        if (notDeletedDestinations.length == 0 && ops) {
          errors.push('At least one destination is required')
        }

        if (ops && (!transaction.ops_year || String(transaction.ops_year).trim()== '')) {
          errors.push('Year is required')
        }

        if (total != 100) {
          errors.push('Total percentage must be 100')
        }

        if (transaction.category == 'Ops Pack' && requireOrderNumber && (transaction.magento_order_id == '' || transaction.magento_order_id == null)) {
          errors.push('Ops Pack transactions must have an order ID')
        }

        if (transaction.direction == "-2" && (transaction.destination_location_id ?? '') == '') {
          errors.push('Transfer transactions must have a target location') 
        }

        if (!transaction.location_id) {
          errors.push('Source location is required')
        }

        if (!transaction.direction) {
          errors.push('You must select a transaction type')
        }

        set({errors})

        return errors.length == 0
      }
    }), {
      name: 'transaction-storage',
      partialize: (state) => {
        return Object.fromEntries(
          Object.entries(state).filter(([key]) => !['collection'].includes(key))
        )
      },
    }
  )
)
